import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { SystemOverviewSubpage } from "@/interfaces/admin/SystemOverviewSubpages";
import ApiService from "@/core/services/ApiService";
import { TransactionVolume } from "@/core/services/api/interfaces/manager/Company";

const hddOptions = {
  chart: {
    height: 1100,
    type: "line",
    dropShadow: {
      enabled: true,
      color: "#000",
      top: 18,
      left: 7,
      blur: 10,
      opacity: 0.2,
    },
    toolbar: {
      show: false,
    },
  },
  colors: ["#ffe435", "#49d9ec"],
  dataLabels: {
    enabled: true,
  },
  stroke: {
    curve: "smooth",
  },
  title: {
    text: "HDD: Total and Free space",
    align: "center",
  },
  grid: {
    borderColor: "#e7e7e7",
    row: {
      colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
      opacity: 0.5,
    },
  },
  markers: {
    size: 1,
  },
  xaxis: {
    categories: [] as string[],
    title: {
      text: "Date and time",
    },
  },
  yaxis: {
    title: {
      text: "Size",
    },
    min: 0,
    max: 1000,
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

const cpuOptions = {
  chart: {
    height: 1100,
    type: "line",
    dropShadow: {
      enabled: true,
      color: "#000",
      top: 18,
      left: 7,
      blur: 10,
      opacity: 0.2,
    },
    toolbar: {
      show: false,
    },
  },
  colors: [
    "#f2f32f",
    "#79ffec",
    "#006993",
    "#f2d7b8",
    "#1958a2",
    "#6fac50",
    "#84700a",
    "#ff0000",
    "#b75b43",
    "#e83488",
    "#8cd13b",
    "#eb8dfe",
    "#001f41",
    "#19a279",
    "#ab7b7b",
    "#187f63",
    "#b33838",
    "#16ff00",
    "#ab6d8b",
    "#00597f",
    "#8dd3fe",
    "#67ed63",
  ],
  dataLabels: {
    enabled: true,
  },
  stroke: {
    curve: "smooth",
  },
  title: {
    text: "CPU: load average",
    align: "center",
  },
  grid: {
    borderColor: "#e7e7e7",
    row: {
      colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
      opacity: 0.5,
    },
  },
  markers: {
    size: 1,
  },
  xaxis: {
    categories: [] as string[],
    title: {
      text: "Date and time",
    },
  },
  yaxis: {
    title: {
      text: "Load average",
    },
    min: 0,
    max: 100,
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

const rabbitmqOptions = {
  chart: {
    height: 1100,
    type: "line",
    dropShadow: {
      enabled: true,
      color: "#000",
      top: 18,
      left: 7,
      blur: 10,
      opacity: 0.2,
    },
    toolbar: {
      show: false,
    },
  },
  colors: [
    "#f2f32f",
    "#79ffec",
    "#006993",
    "#f2d7b8",
    "#1958a2",
    "#6fac50",
    "#84700a",
    "#ff0000",
    "#b75b43",
    "#e83488",
    "#8cd13b",
    "#eb8dfe",
    "#001f41",
    "#19a279",
    "#ab7b7b",
    "#187f63",
    "#b33838",
    "#16ff00",
    "#ab6d8b",
    "#00597f",
    "#8dd3fe",
    "#67ed63",
  ],
  dataLabels: {
    enabled: true,
  },
  stroke: {
    curve: "smooth",
  },
  title: {
    text: "RabbitMQ: queue's loading",
    align: "center",
  },
  grid: {
    borderColor: "#e7e7e7",
    row: {
      colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
      opacity: 0.5,
    },
  },
  markers: {
    size: 1,
  },
  xaxis: {
    categories: [] as string[],
    title: {
      text: "Date and time",
    },
  },
  yaxis: {
    title: {
      text: "Size",
    },
    min: 0,
    max: 100,
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

@Module
export default class SystemOverviewModule extends VuexModule {
  _storage = {
    cpu: null as null | object,
    hdd: null as null | object,
    rabbitmq: null as null | object,
    interface: null as null | object,
  };
  interval = null as null | number;

  transactionVolume: TransactionVolume[] = [];

  get overviewSystem() {
    return (subpage: SystemOverviewSubpage) => {
      return this._storage[subpage];
    };
  }

  get adminStatisticTransactionVolume(): TransactionVolume[] {
    return this.transactionVolume;
  }

  get graph() {
    return (subpage: SystemOverviewSubpage) => {
      if ("hdd" === subpage) {
        return this.hddGraph;
      } else if ("cpu" === subpage) {
        return this.cpuGraph;
      } else if ("rabbitmq" === subpage) {
        return this.rabbitmqGraph;
      }

      return { series: {}, isLoaded: false, options: {} };
    };
  }

  get hddGraph() {
    const diskTotalSpace = [] as number[];
    const diskFreeSpace = [] as number[];
    const categories = [] as string[];
    const options = Object.assign({}, hddOptions);
    const rate = 1024 * 1024 * 1024;
    const isLoaded = null !== this._storage.hdd;
    if (isLoaded) {
      for (const i in this._storage.hdd) {
        const item = this._storage.hdd[i] as {
          diskTotalSpace: number;
          diskFreeSpace: number;
          dateTime: string;
        };
        diskTotalSpace.push(
          parseFloat((item.diskTotalSpace / rate).toFixed(2))
        );
        diskFreeSpace.push(parseFloat((item.diskFreeSpace / rate).toFixed(2)));
        categories.push(item.dateTime);
      }
      options.xaxis.categories = categories;
      const min = Math.min(...[...diskTotalSpace, ...diskFreeSpace]);
      const max = Math.max(...[...diskTotalSpace, ...diskFreeSpace]);

      options.yaxis.min = min - 100;
      options.yaxis.max = max + 100;
    }

    const series = [
      {
        name: "Disk Total Space",
        data: diskTotalSpace,
      },
      {
        name: "Disk Free Space",
        data: diskFreeSpace,
      },
    ];

    return { options, series, isLoaded };
  }

  get cpuGraph() {
    const load = {} as { [index: string]: number[] };
    load["Load Average 1"] = [];
    load["Load Average 5"] = [];
    load["Load Average 15"] = [];
    const categories = [] as string[];
    const options = Object.assign({}, cpuOptions);
    const isLoaded = null !== this._storage.cpu;
    let max = 5;
    if (isLoaded) {
      for (const i in this._storage.cpu) {
        const item = this._storage.cpu[i] as {
          cores: { [index: string]: number };
          loadAverage: number[];
          dateTime: string;
        };
        load["Load Average 1"].push(item.loadAverage[0]);
        load["Load Average 5"].push(item.loadAverage[1]);
        load["Load Average 15"].push(item.loadAverage[2]);
        for (const coreName in item.cores) {
          if (!(coreName in load)) {
            load[coreName] = [];
          }
          const value = item.cores[coreName];
          load[coreName].push(value);
          max = Math.max(value, max);
        }

        categories.push(item.dateTime);
        options.yaxis.min = 0;
        options.yaxis.max = max + 2;
      }
      options.xaxis.categories = categories;
    }

    const series = Object.entries(load).map(([name, data]) => ({ name, data }));

    return { options, series, isLoaded };
  }

  get rabbitmqGraph() {
    const queues = {} as { [index: string]: number[] };
    const categories = [] as string[];
    const options = Object.assign({}, rabbitmqOptions);
    const isLoaded = null !== this._storage.rabbitmq;
    let max = 5;
    if (isLoaded) {
      for (const i in this._storage.rabbitmq) {
        const item = this._storage.rabbitmq[i] as {
          queues: { [index: string]: number };
          dateTime: string;
        };
        for (const queueName in item.queues) {
          if (!(queueName in queues)) {
            queues[queueName] = [];
          }
          const value = item.queues[queueName];
          queues[queueName].push(value);
          max = Math.max(value, max);
        }

        categories.push(item.dateTime);
        options.yaxis.min = -1;
        options.yaxis.max = max + 5;
      }
      options.xaxis.categories = categories;
    }

    const series = Object.entries(queues).map(([name, data]) => ({
      name,
      data,
    }));

    return { options, series, isLoaded };
  }

  @Action({ rawError: true })
  [Actions.ADMIN_SYSTEM_OVERVIEW_START]({ subpage, per_page, intervalTime }) {
    const interval = setInterval(() => {
      return this.context.dispatch(Actions.ADMIN_SYSTEM_OVERVIEW_LOAD, {
        subpage,
        per_page,
      });
    }, intervalTime || 20000);

    this.context.commit(Mutations.ADMIN_SYSTEM_OVERVIEW_SET_INTERVAL, interval);

    return this.context.dispatch(Actions.ADMIN_SYSTEM_OVERVIEW_LOAD, {
      subpage,
      per_page,
    });
  }

  @Action({ rawError: true })
  [Actions.ADMIN_SYSTEM_OVERVIEW_LOAD]({ subpage, per_page }) {
    return ApiService.admin.system
      .overview(subpage, { params: { per_page } })
      .then(({ data }) => {
        this.context.commit(Mutations.ADMIN_SYSTEM_OVERVIEW_SET, {
          subpage,
          data,
        });
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_SYSTEM_STATISTIC_TRANSACTION_VOLUME_LOAD](params?) {
    return ApiService.admin.system
      .statisticTransactionVolume(params)
      .then(ApiService.unwrapItemResponse)
      .then((statistic) =>
        this.context.commit(
          Mutations.ADMIN_SYSTEM_STATISTIC_TRANSACTION_VOLUME_SET,
          statistic as TransactionVolume[]
        )
      );
  }

  @Mutation
  [Mutations.ADMIN_SYSTEM_OVERVIEW_SET]({ subpage, data }) {
    this._storage[subpage] = data;
  }

  @Mutation
  [Mutations.ADMIN_SYSTEM_OVERVIEW_SET_INTERVAL](
    interval: number | null = null
  ) {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = interval;
  }

  @Mutation
  [Mutations.ADMIN_SYSTEM_STATISTIC_TRANSACTION_VOLUME_SET](volume) {
    this.transactionVolume = volume;
  }
}
